import { useRef, useEffect } from "react";
import { Box, Camera, Share2, SlidersHorizontal } from "lucide-react";
import { useSilhouetteExtractor } from "./SilhouetteExtractorContext";
import { CheckboxField } from "../reusable/CheckboxField";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { SendSvgToToolMenu } from "../reusable/SendSvgToToolMenu";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";

export function SilhouetteExtractorControls() {
    const {
        modelName,
        isLoadingModel,
        loadError,
        cameraParams,
        silhouetteParams,
        generatedSvg,
        canExport,
        setCameraParams,
        setSilhouetteParams,
        loadModel,
        setLoadError,
        handleDownload,
        resetParameters,
    } = useSilhouetteExtractor();

    const fileInputRef = useRef<HTMLInputElement>(null);
    const strokeColorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (strokeColorTimeoutRef.current) clearTimeout(strokeColorTimeoutRef.current);
        };
    }, []);

    const handleFloat =
        (setter: (v: number) => void, min = 0, max = Infinity) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min || n > max) return;
            setter(n);
        };

    const handleInt =
        (setter: (v: number) => void, min = 1) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min) return;
            setter(Math.round(n));
        };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setLoadError(null);
            void loadModel(file);
        }
        e.target.value = "";
    };

    const handleStrokeColorPicker = (v: string) => {
        if (strokeColorTimeoutRef.current) clearTimeout(strokeColorTimeoutRef.current);
        strokeColorTimeoutRef.current = setTimeout(() => setSilhouetteParams({ strokeColor: v }), 150);
    };

    return (
        <div className="panel-controls silhouette-extractor-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Silhouette Extractor</h2>
                    <ToolDescriptionPopover dialogLabel="Silhouette Extractor tool description">
                        <p>
                            Import a GLTF/GLB 3D model, orbit it interactively in a live WebGL
                            preview, then extract mathematically exact silhouette edges as a clean
                            SVG output. Silhouette edges are where front-facing and back-facing
                            polygons meet. Optionally include crease/feature edges above a
                            configurable angle threshold.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <button type="button" className="generic-button" onClick={resetParameters}>
                        Reset parameters
                    </button>
                    <button
                        type="button"
                        className="generic-button"
                        disabled={!canExport}
                        onClick={handleDownload}
                    >
                        Download SVG
                    </button>
                </div>
            </div>

            <div className="path-tool-inputs">
                <CollapsibleToolGroup title="Model" icon={Box}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".gltf,.glb,.obj,.fbx"
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                    />
                    <div className="field-row">
                        <button
                            type="button"
                            className="tool-file-btn tool-file-btn--stretch"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isLoadingModel}
                        >
                            <span>
                                {isLoadingModel
                                    ? "Loading…"
                                    : modelName
                                    ? modelName
                                    : "Choose GLTF / GLB…"}
                            </span>
                        </button>
                    </div>
                    <p style={{ margin: "4px 0 0", fontSize: 12, opacity: 0.6 }}>
                        GLTF, GLB, OBJ, and FBX formats. Draco-compressed GLTF is not supported.
                    </p>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Camera" icon={Camera}>
                    <div className="field-row">
                        <label className="field-label">
                            Projection
                            <select
                                value={cameraParams.projectionType}
                                onChange={(e) =>
                                    setCameraParams({
                                        projectionType: e.target.value as "perspective" | "orthographic",
                                    })
                                }
                                style={{ width: "100%", marginTop: 4 }}
                            >
                                <option value="perspective">Perspective</option>
                                <option value="orthographic">Orthographic</option>
                            </select>
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Field of view (°)
                            <DebouncedInput
                                type="number"
                                min={5}
                                max={170}
                                step={1}
                                value={cameraParams.fov}
                                onChange={handleFloat((v) => setCameraParams({ fov: v }), 5, 170)}
                                disabled={cameraParams.projectionType === "orthographic"}
                                debounceMs={300}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Camera distance
                            <DebouncedInput
                                type="number"
                                min={0.1}
                                max={200}
                                step={0.5}
                                value={cameraParams.distance}
                                onChange={handleFloat((v) => setCameraParams({ distance: v }), 0.1, 200)}
                                debounceMs={300}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Silhouette" icon={SlidersHorizontal}>
                    <div className="field-row">
                        <label className="field-label">
                            Stroke color
                            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
                                <input
                                    type="color"
                                    value={silhouetteParams.strokeColor}
                                    onChange={(e) => handleStrokeColorPicker(e.target.value)}
                                    style={{ width: 50, height: 30, minWidth: 30 }}
                                />
                                <DebouncedInput
                                    type="text"
                                    value={silhouetteParams.strokeColor}
                                    onChange={(v) => setSilhouetteParams({ strokeColor: String(v) })}
                                    style={{ flex: 1, padding: 4, fontFamily: "monospace" }}
                                />
                            </div>
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Stroke width
                            <DebouncedInput
                                type="number"
                                min={0.1}
                                max={50}
                                step={0.1}
                                value={silhouetteParams.strokeWidth}
                                onChange={handleFloat((v) => setSilhouetteParams({ strokeWidth: v }), 0.1, 50)}
                                debounceMs={300}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={silhouetteParams.includeCreases}
                            onChange={(v) => setSilhouetteParams({ includeCreases: v })}
                            label="Include crease edges"
                        />
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Crease angle threshold (°)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={180}
                                step={1}
                                value={silhouetteParams.creaseAngleDeg}
                                onChange={handleInt(
                                    (v) => setSilhouetteParams({ creaseAngleDeg: v }),
                                    0,
                                )}
                                disabled={!silhouetteParams.includeCreases}
                                debounceMs={300}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={silhouetteParams.unionPaths}
                            onChange={(v) => setSilhouetteParams({ unionPaths: v })}
                            label="Boolean union paths"
                        />
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Output width (px)
                            <DebouncedInput
                                type="number"
                                min={50}
                                max={4000}
                                step={50}
                                value={silhouetteParams.outputWidth}
                                onChange={handleInt((v) => setSilhouetteParams({ outputWidth: v }), 50)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Output height (px)
                            <DebouncedInput
                                type="number"
                                min={50}
                                max={4000}
                                step={50}
                                value={silhouetteParams.outputHeight}
                                onChange={handleInt((v) => setSilhouetteParams({ outputHeight: v }), 50)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Send to tool" icon={Share2}>
                    <SendSvgToToolMenu
                        disabled={!canExport}
                        getSvg={() =>
                            generatedSvg
                                ? {
                                      name: `silhouette-${modelName.replace(/\.[^.]+$/, "") || "export"}.svg`,
                                      content: generatedSvg,
                                  }
                                : null
                        }
                        allowedSlots={["primary"]}
                        excludeTargetPanels={["silhouette-extractor"]}
                    />
                </CollapsibleToolGroup>

                {loadError && (
                    <p className="upload-error" style={{ margin: "8px 0 0" }}>
                        {loadError}
                    </p>
                )}
            </div>
        </div>
    );
}
